import { useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { useWork } from '~/components/use-work'
import { AmountBar } from '~/components/amount-bar'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { FoodDials } from '~/components/food-dials'
import { FoodFormDialog } from '~/components/food-form-dialog'
import { FoodMark } from '~/components/food-mark'
import { FoodName } from '~/components/food-name'
import {
  CopyIcon,
  DeleteIcon,
  EditIcon,
  EyeIcon,
  SendIcon,
  ShareIcon,
  StarIcon,
} from '~/components/icons'
import { HeaderAction, PageDialog } from '~/components/page-dialog'
import { useToast } from '~/components/toast-context'
import { WhenPill } from '~/components/when-pill'
import { useApi, useDataService, useDataState } from '~/data/data-context'
import { entriesOfFood, useFavouriteFoods } from '~/data/food-store'
import {
  MACROS,
  NUTRIENTS,
  NUTRIENT_LABELS,
  amountSaid,
  broughtFromSaid,
  dayTotal,
  foodKey,
  formatCalories,
  formatNutrient,
  librarySaid,
  nowTime,
  nutrientsFor,
  referenceSaid,
  type Amount,
  type Entry,
  type Food,
  type GoalOnDay,
  type NutritionData,
} from '~/data/nutrition'
import { lastLoggedAmountHeld } from '~/data/nutrition-store'
import { todayKey } from '~/data/weighins'
import { dayStart, formatDayLabel } from '~/lib/dates'
import { openingAmount } from '~/lib/opening-amount'
import { handOver, saidOf, sentFromFood, sharedFromFood, type ShareOutcome } from '~/lib/share-food'

/** The nutrients past the four the dials draw. */
const MICRO = NUTRIENTS.filter(
  (name) => name !== 'calories' && !(MACROS as readonly string[]).includes(name),
)

/** The day a page logs onto, or the entry it is about, with what the day holds around it. */
interface OntoDay {
  entries: readonly Entry[]
  goal: GoalOnDay | null
  /** The diary, for the calendar the day is picked on; null draws the days bare. */
  data: NutritionData | null
}

/**
 * A food's page — the nutrition spec, A food's page: one page however it was reached and
 * whatever is behind it. It opens on what it is about — the food's mark, its name, and under
 * that what it is. Then what the amount comes to on the dials a food is read on everywhere,
 * the rest of its nutrients, the code on its packet, and the times it was logged before; and
 * along the foot, where it logs onto a day or is an entry already on one, the amount being
 * typed, when it lands, and the key that logs it or keeps the change.
 *
 * What can be done to the food stands in the heading: marking it a favourite, sharing it,
 * and opening it — an own food to be changed, a library food to be read — and, on an entry,
 * taking the entry off its day.
 */
export function FoodSheet({
  food,
  mine = false,
  lastAmount = null,
  logging,
  editing,
  onClose,
  onSaved,
  onMarked,
}: {
  food: Food
  /** Whether the account owns it, which decides whether it opens to read or to edit. */
  mine?: boolean
  /** The amount the list it was opened from knows it was last logged in, where it knows one. */
  lastAmount?: Amount | null
  /** The day the food is being logged onto, where the page is the way to log it. */
  logging?:
    | (OntoDay & {
        day: string
        time: string
        onDay: (next: string) => void
        onTime: (next: string) => void
        /** Logs it, and never waits: the key that logs is not held down for an answer. */
        onLog: (amount: Amount) => void
      })
    | undefined
  /** An entry already logged, where the page is the entry opened again. */
  editing?:
    | (OntoDay & {
        entry: Entry
        /** Keeps the change, and is waited on: the key says it is working until it lands. */
        onSave: (amount: Amount, day: string, time: string) => Promise<void>
        /** Takes it off the day, waited on the same way from the confirmation. */
        onRemove: () => Promise<void>
      })
    | undefined
  onClose: () => void
  /** A copy of a library food, or an edit of your own, once it is saved. */
  onSaved?: ((saved: Food) => void) | undefined
  /** Told when it is made a favourite or unmade, so whatever lists it goes on read again. */
  onMarked?: (() => void) | undefined
}) {
  const api = useApi()
  const toast = useToast()
  const [opened, setOpened] = useState(false)
  /** Whether it is one of the marked ones; null until this device has read the account. */
  const held = useFavouriteFoods()
  const marked = held === null ? null : held.some((each) => foodKey(each) === foodKey(food))
  const [marking, setMarking] = useState(false)
  /** Which of the two hand-overs is under way — each works on its own key. */
  const [sharing, setSharing] = useState<'send' | 'share' | null>(null)
  const keeping = useWork()
  const [leaving, setLeaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const { session } = useAuth()
  const [amount, setAmount] = useState<Amount>(
    () =>
      editing?.entry.amount ??
      openingAmount(food, lastAmount ?? lastLoggedAmountHeld(foodKey(food))),
  )
  /** When an entry opened again stands, changed on the foot; logging keeps its own. */
  const [stood, setStood] = useState(() => ({
    day: editing?.entry.day ?? '',
    time: editing?.entry.time ?? '',
  }))
  const service = useDataService()
  const allEntries = useDataState().entries

  /*
   * The mark lands in the mirror first and is sent after, so the star answers the finger
   * rather than the connection. A send that fails is put back here rather than left to the
   * next pull: the row never moved on the server, so the feed carries nothing about it and a
   * pull would leave the wrong mark standing for good.
   */
  const mark = async () => {
    if (marked === null) {
      return
    }
    setMarking(true)
    const key = foodKey(food)
    // The row as it stands, so undoing a failed unmarking puts back the mark that was there —
    // with the day it was made on — rather than a fresh one that would jump the list.
    const held = service.getState().favourites?.find((each) => each.key === key)
    try {
      if (marked) {
        await service.drop('favourite', key)
        await api.removeFavourite(key)
      } else {
        await service.keep('favourite', key, { key, food, createdAt: Date.now() })
        await api.addFavourite(key, food)
      }
      onMarked?.()
    } catch {
      if (marked && held !== undefined) {
        await service.keep('favourite', key, held)
      } else if (!marked) {
        await service.drop('favourite', key)
      }
      toast('Favourites need a connection.')
    } finally {
      setMarking(false)
    }
  }

  /**
   * The moment sending carries. The day and time the page already stands on where it stands on
   * one — an entry being looked at again, a food being logged onto a day — so what arrives
   * lands where it was eaten rather than whenever it happened to be opened.
   */
  const sendingDay = logging?.day ?? editing?.entry.day ?? null
  const sendingTime = logging?.time ?? (stood.time === '' ? null : stood.time)

  /** What a handing-over said, once it has been handed over. */
  const reportHandOver = (outcome: ShareOutcome, verb: 'shared' | 'sent') => {
    if (outcome === 'copied') {
      toast('Link copied')
    } else if (outcome === 'too-large') {
      toast(`There is too much here to ${verb === 'sent' ? 'send' : 'share'} as a link.`)
    } else if (outcome === 'failed') {
      toast(`That could not be ${verb}.`)
    }
  }

  /**
   * The food itself handed to somebody to keep — the nutrition spec, Sharing from the app.
   * A blueprint: what arrives is the thing, and the key on the other side keeps it.
   */
  const share = async () => {
    if (sharing !== null) return
    setSharing('share')
    try {
      const sharedAmount = openingAmount(food, null)
      const shared = sharedFromFood(food, sharedAmount, session?.user.name ?? null)
      /*
       * What this much of it comes to, not what a hundred grams of it does: the link opens on
       * this amount, and a message stating the reference amount beside it says two different
       * things about one share. A recipe is the exception — it travels as a recipe, and what
       * it comes to is a serving of it.
       */
      const comesTo = food.recipe === null ? nutrientsFor(food, sharedAmount) : food.nutrients
      const outcome = await handOver(shared, saidOf(shared, comesTo, session?.user.name ?? null))
      reportHandOver(outcome, 'shared')
    } finally {
      setSharing(null)
    }
  }

  /**
   * This much of it handed to somebody to log — the nutrition spec, Sending from the app.
   * Logging done for somebody else, so it carries the amount standing on the page and the
   * moment it is to land on, and the key on the other side logs rather than keeps.
   */
  const send = async () => {
    if (sharing !== null) return
    setSharing('send')
    try {
      const sending = amount
      // What this much of it comes to, which is the line the message says under the name —
      // the nutrition spec, Handing it over.
      const comesTo = nutrientsFor(food, sending) ?? food.nutrients
      const shared = sentFromFood(food, sending, comesTo, session?.user.name ?? null, {
        day: sendingDay ?? todayKey(),
        time: sendingTime ?? nowTime(),
      })
      const outcome = await handOver(shared, saidOf(shared, comesTo, session?.user.name ?? null))
      reportHandOver(outcome, 'sent')
    } finally {
      setSharing(null)
    }
  }

  const library = food.source === 'library'
  const canOpen = library || mine
  /** The day the page is on, one way or the other. */
  const onto = logging ?? editing ?? null
  /* What the page's figures are of: this much of the food where it stands on a day, and the
     reference amount otherwise. */
  const comesTo = onto === null ? food.nutrients : nutrientsFor(food, amount)
  /* The rest of the day, for what it would have left — without this entry, where it is one. */
  const dayWithout =
    onto === null
      ? null
      : dayTotal(
          editing === undefined
            ? onto.entries
            : onto.entries.filter((held) => held.id !== editing.entry.id),
        )
  const before =
    onto === null
      ? []
      : entriesOfFood(allEntries ?? [], foodKey(food))
          .filter((held) => held.id !== editing?.entry.id)
          .slice(0, 5)

  /* An entry opened again: what has been changed on it, which closing would lose. */
  const dirty =
    editing !== undefined &&
    (JSON.stringify(amount) !== JSON.stringify(editing.entry.amount) ||
      stood.day !== editing.entry.day ||
      stood.time !== editing.entry.time)

  const askToClose = () => {
    if (dirty) {
      setLeaving(true)
    } else {
      onClose()
    }
  }

  return (
    <PageDialog
      title={editing?.entry.name ?? food.name}
      titleBelow
      onClose={askToClose}
      className="max-w-md"
      fixedHeight={onto !== null}
      actions={
        <>
          {/* Marked or not — the nutrition spec, Favourites. Nothing is claimed either way
              until the list has arrived. */}
          {marked !== null && (
            <HeaderAction
              label={marked ? 'Remove from favourites' : 'Add to favourites'}
              Icon={(props) => <StarIcon {...props} filled={marked} />}
              pressed={marked}
              accent={marked}
              disabled={marking}
              onPress={() => {
                void mark()
              }}
            />
          )}
          {/*
            Two acts side by side, which is the one page where they genuinely differ: sending
            hands over this much of it for somebody to log, sharing hands over the food itself
            for them to keep — the nutrition spec, Sending and sharing.
          */}
          <HeaderAction
            label="Send"
            Icon={SendIcon}
            disabled={sharing === 'share'}
            working={sharing === 'send'}
            onPress={() => {
              void send()
            }}
          />
          <HeaderAction
            label="Share"
            Icon={ShareIcon}
            disabled={sharing === 'send'}
            working={sharing === 'share'}
            onPress={() => {
              void share()
            }}
          />
          {canOpen && (
            <HeaderAction
              label={library ? 'Read this food' : 'Edit this food'}
              Icon={library ? EyeIcon : EditIcon}
              onPress={() => {
                setOpened(true)
              }}
            />
          )}
          {/* An entry comes off its day from its heading, and asks first. */}
          {editing !== undefined && (
            <HeaderAction
              label="Remove"
              Icon={DeleteIcon}
              danger
              disabled={keeping.working}
              onPress={() => {
                setRemoving(true)
              }}
            />
          )}
        </>
      }
      {...(onto === null
        ? {}
        : {
            /*
             * The panel along the foot, part of the page's chrome rather than of what scrolls:
             * the amount typed with its unit over the units it can be counted in, and at the
             * end of that same row when what is logged lands, and the key that logs — or, on
             * an entry, keeps. One panel, so nothing in it is boxed off from the rest.
             */
            foot: (
              <AmountBar
                food={food}
                amount={amount}
                onAmount={setAmount}
                busy={keeping.working}
                beside={
                  <>
                    {logging !== undefined ? (
                      <WhenPill
                        day={logging.day}
                        time={logging.time}
                        data={logging.data}
                        onDay={logging.onDay}
                        onTime={logging.onTime}
                      />
                    ) : (
                      <WhenPill
                        day={stood.day}
                        time={stood.time}
                        data={onto.data}
                        onDay={(next) => {
                          setStood((held) => ({ ...held, day: next }))
                        }}
                        onTime={(next) => {
                          setStood((held) => ({ ...held, time: next }))
                        }}
                      />
                    )}
                    <span className="flex-1" />
                    {logging !== undefined ? (
                      <button
                        type="button"
                        disabled={amount.value <= 0}
                        className="shrink-0 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-ink shadow-2xl shadow-black/40 disabled:opacity-50"
                        onClick={() => {
                          logging.onLog(amount)
                        }}
                      >
                        Log
                      </button>
                    ) : (
                      editing !== undefined && (
                        <button
                          type="button"
                          aria-busy={keeping.working || undefined}
                          disabled={amount.value <= 0}
                          className="relative shrink-0 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-ink shadow-2xl shadow-black/40 disabled:opacity-50"
                          onClick={() => {
                            void keeping
                              .run(() => editing.onSave(amount, stood.day, stood.time))
                              .catch(() => undefined)
                          }}
                        >
                          <Working shown={keeping.shown}>Save</Working>
                        </button>
                      )
                    )}
                  </>
                }
              />
            ),
          })}
    >
      <div className="space-y-8">
        {/* The food names its own page, under the heading: its mark, its name, and what it is. */}
        <div className="flex items-center gap-3">
          <FoodMark food={food} size="size-12 text-2xl" />
          <div className="min-w-0 flex-1">
            {/* A name is never cut to fit: it takes a second line, and only then gives up. */}
            <p className="line-clamp-2 text-2xl font-medium">
              <FoodName food={food} />
            </p>
            <p className="truncate text-sm text-ink-muted">
              {formatCalories(food.nutrients.calories)} per {referenceSaid(food)}
              {library ? ` · ${librarySaid(food)}` : ' · Mine'}
            </p>
          </div>
        </div>

        {/* What it comes to — this much of it where it stands on a day, and per its reference
            amount otherwise — on the dials a food is read on everywhere. */}
        <FoodDials
          comesTo={comesTo}
          dayTotalWithout={dayWithout}
          goal={onto?.goal ?? null}
          label={onto === null ? `Per ${referenceSaid(food)}` : 'This comes to'}
        />

        {/*
         * Where the food came from, on the one page that says it. A list says only whether a
         * food is the account's, which is what a badge there is for; this is the page you open
         * to find out what a food actually is, and an imported one's numbers are another app's.
         */}
        {broughtFromSaid(food) !== null && (
          <p className="text-sm text-ink-muted">{broughtFromSaid(food)}</p>
        )}

        <section aria-label="Nutrients" className="space-y-2">
          <h3 className="px-1 text-xs font-medium tracking-wider text-ink-muted uppercase">
            Nutrients
          </h3>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {MICRO.map((name) => (
              <div key={name} className="rounded-xl bg-surface-raised px-3 py-2">
                <dt className="text-xs text-ink-muted">{NUTRIENT_LABELS[name]}</dt>
                <dd className="font-medium">
                  {comesTo?.[name] == null ? '—' : formatNutrient(name, comesTo[name])}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/*
         * The code on the packet, where the food carries one. It is the one thing here worth
         * having somewhere else, so pressing it takes a copy rather than leaving it to be read
         * off the screen a digit at a time.
         */}
        {food.barcode !== null && food.barcode !== '' && (
          <button
            type="button"
            aria-label={`Barcode ${food.barcode}, copy`}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-hover"
            onClick={() => {
              void navigator.clipboard
                .writeText(food.barcode ?? '')
                .then(() => {
                  toast('Barcode copied')
                })
                .catch(() => {
                  toast('That could not be copied.')
                })
            }}
          >
            <span className="flex-1 truncate">{food.barcode}</span>
            <CopyIcon aria-hidden className="size-4 shrink-0" />
          </button>
        )}

        {/* The last handful of times it was logged, and how much: pressing one takes its amount. */}
        {before.length > 0 && (
          <section aria-label="Logged before" className="space-y-0.5">
            <h3 className="px-1 text-xs font-medium tracking-wider text-ink-muted uppercase">
              Logged before
            </h3>
            <ul className="space-y-0.5">
              {before.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    aria-label={`${amountSaid(entry.amount)} on ${formatDayLabel(dayStart(entry.day))}`}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-hover"
                    onClick={() => {
                      setAmount(entry.amount)
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {formatDayLabel(dayStart(entry.day))} · {entry.time}
                    </span>
                    <span className="shrink-0 text-xs text-ink-muted">
                      {amountSaid(entry.amount)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {opened && (
        <FoodFormDialog
          food={food}
          onClose={() => {
            setOpened(false)
          }}
          onSaved={(saved) => {
            setOpened(false)
            onSaved?.(saved)
            onClose()
          }}
        />
      )}
      {leaving && (
        <ConfirmDialog
          title="Discard your changes?"
          body="What you have changed here is not saved."
          confirm="Discard"
          onConfirm={onClose}
          onDismiss={() => {
            setLeaving(false)
          }}
        />
      )}
      {removing && editing !== undefined && (
        <ConfirmDialog
          title={`Remove ${editing.entry.name}?`}
          body="It comes off the day, and the day's total follows."
          confirm="Remove"
          onConfirm={editing.onRemove}
          onDismiss={() => {
            setRemoving(false)
          }}
        />
      )}
    </PageDialog>
  )
}
